/* eslint-disable complexity */
import {QuestionType} from 'common/constants/survey';
import {QuestionValue, SurveyValue} from 'common/models/survey';
import {isNil} from 'lodash';
import isEmpty from 'ui/utils/isEmpty';

import isDateRange, {isValidDateRange} from './isDateRange';
import isValidStringDate from './isValidStringDate';

// https://docs.yandex-team.ru/cloud/forms/get-params
export const parseValue = (
    questionType: QuestionType,
    value: string | string[] | Record<string, string>,
): SurveyValue | null => {
    switch (questionType) {
        case QuestionType.Matrix: {
            if (Array.isArray(value) && !isNil(value[0])) {
                value = value[0];
            }
            if (typeof value === 'object' && value !== null) {
                // postMessage
                return Object.entries(value).map(([row, rowColumn]) => ({
                    row,
                    column: rowColumn.split('_')[1],
                }));
            }
            const [row, column] = value.split('_');
            if (row && column) {
                return [{row, column}];
            }
            return null;
        }
        case QuestionType.Suggest:
        case QuestionType.Checkbox:
        case QuestionType.Radio:
        case QuestionType.Stars:
        case QuestionType.OneRow:
        case QuestionType.Dropdown: {
            if (typeof value === 'string') {
                return [value];
            }
            if (Array.isArray(value) && typeof value[0] === 'string') {
                return value;
            }
            return null;
        }
        case QuestionType.String:
        case QuestionType.Payment: {
            if (typeof value === 'string') {
                return value;
            }
            if (Array.isArray(value) && typeof value[0] === 'string') {
                return value[0];
            }
            return null;
        }
        case QuestionType.Integer: {
            if (Array.isArray(value) && typeof value[0] === 'string' && !isNaN(Number(value[0]))) {
                return Number(value[0]);
            }
            if (typeof value === 'string' && !isNaN(Number(value))) {
                return Number(value);
            }
            return null;
        }
        case QuestionType.Boolean: {
            return value === 'True';
        }
        case QuestionType.Date: {
            if (isValidStringDate(value)) {
                return value;
            }
            return null;
        }
        case QuestionType.DateRange: {
            if (Array.isArray(value) && value.every(isValidStringDate)) {
                return {
                    begin: value[0],
                    end: value[1],
                };
            }
            if (isValidStringDate(value)) {
                return {
                    begin: value,
                    end: '',
                };
            }
            if (isValidDateRange(value)) {
                return value;
            }
            return null;
        }
        default:
            return null;
    }
};

export const getParsedField = (
    id: string,
    value: string | string[] | Record<string, string>,
    questionsMap: Record<string, QuestionType>,
) => {
    const start = id.indexOf('[');
    const end = id.indexOf(']');
    const [questionSlug, series] = (id.substring(0, start) + id.substring(end + 1)).split('__');

    const questionType = questionsMap[questionSlug];

    if (!questionType) {
        return {[questionSlug]: null};
    }

    const parsedValue = parseValue(questionType, value);

    if (isNil(parsedValue)) {
        return {[questionSlug]: null};
    }
    if (series) {
        return {[questionSlug]: [{index: series, value: parsedValue}]};
    }
    return {[questionSlug]: parsedValue};
};

interface SeriesIndexValue {
    index: number;
    value: QuestionValue;
}

const isSeriesIndexValueArray = (value: unknown): value is SeriesIndexValue[] => {
    return Array.isArray(value) && (value[0] as SeriesIndexValue)?.index !== undefined;
};

const isStringArray = (value: QuestionValue): value is string[] => {
    return (
        Array.isArray(value) &&
        value.length > 0 &&
        (value as unknown[]).every((item): item is string => typeof item === 'string')
    );
};

// https://docs.yandex-team.ru/cloud/forms/get-params
export const getSearchParamsValues = (
    searchParams: URLSearchParams,
    questionTypeMap: Record<string, QuestionType>,
): Record<string, QuestionValue | QuestionValue[]> => {
    const values: Record<string, QuestionValue | SeriesIndexValue[] | QuestionValue[]> = {};

    for (const [key, value] of searchParams) {
        if (isEmpty(value)) {
            continue;
        }
        const field = getParsedField(key, value, questionTypeMap);

        for (const [slug, fieldValue] of Object.entries(field)) {
            if (isNil(fieldValue) || fieldValue === '') {
                continue;
            }

            const currValue = values[slug];
            if (currValue && Array.isArray(currValue)) {
                currValue.push(...fieldValue);
            } else if (typeof currValue === 'object' && isDateRange(currValue)) {
                currValue.end = fieldValue.begin;
            } else {
                values[slug] = fieldValue;
            }
        }
    }

    const result: Record<string, QuestionValue | QuestionValue[]> = {};
    for (const [key, value] of Object.entries(values)) {
        if (isSeriesIndexValueArray(value)) {
            result[key] = value.reduce<QuestionValue[]>((acc, item) => {
                const currValue = acc[item.index];
                const fieldValue = item.value;
                if (isStringArray(fieldValue) && isStringArray(currValue)) {
                    currValue.push(...fieldValue);
                    acc[item.index] = currValue;
                } else {
                    acc[item.index] = item.value;
                }
                return acc;
            }, []);
        } else {
            result[key] = value;
        }
    }
    return result;
};
